/** The notices above the Decide columns: an edit dropped for a teammate's lock, a failed review mark, a failed save, an edit the server refused and the page put back, edits still unsaved at review. */
import { GxErrorBanner, GxNotice } from "../theme/primitives";
import type { ComponentProps } from "react";
import type { ContentCatalog } from "@bsim/contracts";
import { errorSentence } from "../errors";
import type { Refusal } from "./useDraft";
import { decisionIdOf, decisionTitle } from "./decisionValues";

/** "Dividend per share wasn't saved, so it is back at its saved setting: <why>". The server's own sentence is used for a step limit or a card hold (both are plain and name the rule); other refusals use the page's. */
export function refusalText(r: Refusal, catalog: ContentCatalog): string {
  const names = r.decisionIds.map((id) => decisionTitle(catalog.decisions, id as never));
  const plain = (r.error.code === "LEVER_NOT_IN_LEVEL" && r.error.message.startsWith("In guided play")) || r.error.code === "LEVER_CONSTRAINED";
  const why = plain ? r.error.message : errorSentence(r.error);
  return `${names.length === 0 ? "That change" : names.join(", ")} wasn't saved, so it is back at its saved setting and is not in the forecast. ${why}`;
}

/** The decisions still waiting to be saved, by name: "Dividend per share isn't saved yet, so the plan can't go to review." */
export function unsavedLine(paths: readonly string[], catalog: ContentCatalog): string {
  const names = [...new Set(paths.map((p) => (p === "thesis" ? "Your thesis line" : decisionTitle(catalog.decisions, decisionIdOf(p) as never))))];
  return `${names.join(", ")} ${names.length === 1 ? "isn't" : "aren't"} saved yet, so the plan can't go to review. Try again, or put ${names.length === 1 ? "it" : "them"} back.`;
}

export function DecideNotices({ dropped, marks, error, refused = null, unsaved = null }: { readonly dropped: string | null; readonly marks: string | null; readonly error: NonNullable<ComponentProps<typeof GxErrorBanner>["error"]> | null; readonly refused?: string | null; readonly unsaved?: string | null }) {
  return (
    <>
      {refused === null ? null : <div style={{ marginBottom: 12 }}><GxNotice>{refused}</GxNotice></div>}
      {unsaved === null ? null : <div style={{ marginBottom: 12 }}><GxNotice>{unsaved}</GxNotice></div>}
      {dropped === null ? null : <div style={{ marginBottom: 12 }}><GxNotice>{dropped}</GxNotice></div>}
      {marks === null ? null : <div style={{ marginBottom: 12 }}><GxNotice>{marks}</GxNotice></div>}
      {error === null ? null : <div style={{ marginBottom: 12 }}><GxErrorBanner error={error} /></div>}
    </>
  );
}
